import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import AnimatedReaction from './AnimatedReaction';

const Container = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 20px;
  margin-top: 4px;
  min-height: 20px;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

// Helper to normalize reaction types and extract emoji
const normalizeReaction = (reactionType) => {
  // Handle new format: unicode:emoji
  if (reactionType.startsWith('unicode:')) {
    return {
      emoji: reactionType.slice(8),
      normalized: reactionType
    };
  }
  
  // Handle old 'heart' format for backwards compatibility
  if (reactionType === 'heart') {
    return {
      emoji: '❤️',
      normalized: 'unicode:❤️'
    };
  }
  
  // Shouldn't happen, but safe fallback for any unknown format
  return {
    emoji: reactionType,
    normalized: `unicode:${reactionType}`
  };
};

const OverlappingReactions = ({ reactions = {}, userReactions = {} }) => {
  const [emojiInstances, setEmojiInstances] = useState([]);
  const [removingIds, setRemovingIds] = useState(new Set());
  const prevCountsRef = useRef({});
  const rotationCache = useRef({});

  useEffect(() => {
    const newInstances = [];
    let currentOffset = 0;
    
    // Track what's changed
    const prevCounts = prevCountsRef.current;
    const toRemove = new Set();
    
    // Build new instances
    Object.entries(reactions).forEach(([reactionType, count]) => {
      if (count <= 0) return;
      
      const { emoji } = normalizeReaction(reactionType);
      const prevCount = prevCounts[reactionType] || 0;
      
      for (let i = 0; i < count; i++) {
        const key = `${reactionType}-${i}`;
        
        // Cache rotation for consistency
        if (!rotationCache.current[key]) {
          rotationCache.current[key] = (Math.random() - 0.5) * 30;
        }
        
        // Create unique ID for each instance that changes when count increases
        // Use a stable identifier based on whether this is a new reaction
        const uniqueId = `${key}-${i < prevCount ? 'existing' : `new-${prevCount}`}`;
        
        newInstances.push({
          id: uniqueId,
          key,
          emoji,
          rotation: rotationCache.current[key],
          leftOffset: currentOffset,
          zIndex: newInstances.length,
          reactionType,
          isNew: i >= prevCount
        });
        
        currentOffset += 8;
      }
    });
    
    // Handle removals - find instances that should be removed
    emojiInstances.forEach(instance => {
      const currentCount = reactions[instance.reactionType] || 0;
      const index = parseInt(instance.key.split('-')[1]);
      
      if (index >= currentCount) {
        toRemove.add(instance.id);
        
        // Remove after animation
        setTimeout(() => {
          setRemovingIds(prev => {
            const next = new Set(prev);
            next.delete(instance.id);
            return next;
          });
        }, 300);
      }
    });
    
    // Update removing IDs
    if (toRemove.size > 0) {
      setRemovingIds(prev => new Set([...prev, ...toRemove]));
      
      // Delay updating instances to allow removal animation
      setTimeout(() => {
        setEmojiInstances(newInstances);
      }, 300);
    } else {
      setEmojiInstances(newInstances);
    }
    
    // Update prev counts
    prevCountsRef.current = Object.entries(reactions).reduce((acc, [type, count]) => {
      acc[type] = count;
      return acc;
    }, {});
    
  }, [reactions]);

  // Include removing instances in display
  const displayInstances = emojiInstances.map(instance => ({
    ...instance,
    isRemoving: removingIds.has(instance.id)
  }));

  if (displayInstances.length === 0) {
    return null;
  }

  const containerWidth = Math.max(...displayInstances.map(i => i.leftOffset)) + 24;

  return (
    <Container style={{ width: containerWidth }}>
      {displayInstances.map((instance) => (
        <AnimatedReaction
          key={instance.id}
          uniqueKey={instance.id}
          emoji={instance.emoji}
          rotation={instance.rotation}
          leftOffset={instance.leftOffset}
          zIndex={instance.zIndex}
          isRemoving={instance.isRemoving}
        />
      ))}
    </Container>
  );
};

export default OverlappingReactions;